import { doc, setDoc, serverTimestamp } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { LEGAL_VERSIONS } from '../content/legal/versions'
import type { UserProfile, UserConsent } from '../types/schema'

export interface ConsentNoticeDetails {
  needsNotice: boolean
  termsChanged: boolean
  privacyChanged: boolean
}

/**
 * Records user consent on users/{uid}.consent with the active terms and privacy versions.
 * Uses merge: true so no other user profile fields are impacted.
 */
export const recordConsent = async (uid: string): Promise<void> => {
  if (!uid || typeof uid !== 'string' || !uid.trim()) {
    throw new Error('Cannot record consent without a valid user ID.')
  }

  const userRef = doc(firestoreDb, 'users', uid.trim())
  const consentData: UserConsent = {
    termsVersion: LEGAL_VERSIONS.terms,
    privacyVersion: LEGAL_VERSIONS.privacy,
    acceptedAt: serverTimestamp(),
  }

  await setDoc(userRef, { consent: consentData }, { merge: true })
}

/**
 * Determines whether a user requires a one-time notice dialog due to version bump.
 * Returns false on first sign-in (when consent does not yet exist and is auto-recorded),
 * or when the user's existing consent matches current LEGAL_VERSIONS.
 */
export const needsConsentNotice = (
  profile: Partial<UserProfile> | null | undefined,
): boolean => {
  if (!profile?.consent) return false
  const { termsVersion, privacyVersion } = profile.consent
  return termsVersion !== LEGAL_VERSIONS.terms || privacyVersion !== LEGAL_VERSIONS.privacy
}

/**
 * Returns detailed flags on which legal document version(s) changed.
 */
export const getConsentNoticeDetails = (
  profile: Partial<UserProfile> | null | undefined,
): ConsentNoticeDetails => {
  if (!profile?.consent) {
    return {
      needsNotice: false,
      termsChanged: false,
      privacyChanged: false,
    }
  }

  const termsChanged = profile.consent.termsVersion !== LEGAL_VERSIONS.terms
  const privacyChanged = profile.consent.privacyVersion !== LEGAL_VERSIONS.privacy

  return {
    needsNotice: termsChanged || privacyChanged,
    termsChanged,
    privacyChanged,
  }
}
